import * as React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  FormControlLabel,
  FormHelperText,
  IconButton as MuiIconButton,
  Radio,
  RadioGroup,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { Switch } from '../../components/switch/Switch';
import { DiscountTimeWindow, hhmm, Weekday, WEEKDAYS } from './discountTypes';

const WORKDAYS: Weekday[] = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY'];
const WEEKEND: Weekday[] = ['SATURDAY', 'SUNDAY'];
const DEFAULT_INTERVAL = { startTime: '12:00', endTime: '14:00' };
const ALL_DAY = [{ startTime: null, endTime: null }];

type Interval = Omit<DiscountTimeWindow, 'weekday'>;

interface DiscountScheduleEditorProps {
  value: DiscountTimeWindow[];
  onChange: (windows: DiscountTimeWindow[]) => void;
  timeZone?: string | null;
  error?: string;
}

const intervalsKey = (intervals: Interval[]) =>
  intervals
    .map(({ startTime, endTime }) => `${hhmm(startTime)}-${hhmm(endTime)}`)
    .sort()
    .join('|');

const isAllDay = (intervals: Interval[]) =>
  intervals.length > 0 && intervals.every((i) => i.startTime == null && i.endTime == null);

const sameDays = (a: Weekday[], b: Weekday[]) =>
  a.length === b.length && a.every((day) => b.includes(day));

const labelSx = { fontSize: 'var(--text-sm)', fontWeight: 500, color: 'text.secondary' };

interface IntervalsEditorProps {
  intervals: Interval[];
  onChange: (intervals: Interval[]) => void;
  /** Etykieta po lewej (nazwa dnia) w trybie "rozne godziny w rozne dni". */
  dayLabel?: string;
}

/** "Caly dzien" albo lista przedzialow od-do - dla wszystkich dni naraz albo jednego dnia. */
const IntervalsEditor = ({ intervals, onChange, dayLabel }: IntervalsEditorProps) => {
  const { t } = useTranslation();
  const allDay = isAllDay(intervals);

  const update = (index: number, field: 'startTime' | 'endTime', time: string) =>
    onChange(
      intervals.map((interval, i) =>
        i === index ? { ...interval, [field]: time || null } : interval,
      ),
    );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: { xs: 'stretch', sm: 'flex-start' },
        gap: { xs: 'var(--space-2)', sm: 'var(--space-4)' },
      }}
    >
      {dayLabel && (
        <Typography
          sx={{ fontSize: 'var(--text-sm)', fontWeight: 600, minWidth: 110, pt: { sm: '9px' } }}
        >
          {dayLabel}
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', flex: 1 }}>
        <FormControlLabel
          sx={{ m: 0, minHeight: 40 }}
          control={
            <Switch
              checked={allDay}
              onChange={(e) => onChange(e.target.checked ? ALL_DAY : [DEFAULT_INTERVAL])}
            />
          }
          label={
            <Typography sx={{ fontSize: 'var(--text-sm)', ml: 'var(--space-2)' }}>
              {t('discounts.scheduleEditor.allDay')}
            </Typography>
          }
        />
        {!allDay && (
          <>
            {intervals.map((interval, index) => (
              <Box
                key={index}
                sx={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}
              >
                <TextField
                  type="time"
                  size="small"
                  aria-label={t('discounts.scheduleEditor.from')}
                  value={hhmm(interval.startTime)}
                  onChange={(e) => update(index, 'startTime', e.target.value)}
                  sx={{ width: 130 }}
                />
                <Typography sx={{ color: 'text.secondary' }}>–</Typography>
                <TextField
                  type="time"
                  size="small"
                  aria-label={t('discounts.scheduleEditor.to')}
                  value={hhmm(interval.endTime)}
                  onChange={(e) => update(index, 'endTime', e.target.value)}
                  sx={{ width: 130 }}
                />
                {intervals.length > 1 && (
                  <Tooltip title={t('discounts.scheduleEditor.removeInterval')}>
                    <MuiIconButton
                      size="small"
                      onClick={() => onChange(intervals.filter((_, i) => i !== index))}
                      aria-label={t('discounts.scheduleEditor.removeInterval')}
                    >
                      <CloseIcon fontSize="small" />
                    </MuiIconButton>
                  </Tooltip>
                )}
              </Box>
            ))}
            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => onChange([...intervals, DEFAULT_INTERVAL])}
                sx={{ textTransform: 'none' }}
              >
                {t('discounts.scheduleEditor.addInterval')}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

/**
 * Dni tygodnia i przedzialy godzin rabatu. Kontrolowany komponent bez wywolan API:
 * brak przedzialow = rabat dziala zawsze (w ramach dat), przedzial bez godzin = caly dzien.
 *
 * Domyslnie wszystkie zaznaczone dni maja te same godziny (najczestszy przypadek: "lunch
 * w dni robocze 12-15"). Rozne godziny per dzien to opcja, ktora pokazuje wiersz
 * tylko dla zaznaczonych dni.
 */
export const DiscountScheduleEditor = ({
  value,
  onChange,
  timeZone,
  error,
}: DiscountScheduleEditorProps) => {
  const { t } = useTranslation();

  const windowsOf = (day: Weekday): Interval[] =>
    value
      .filter((window) => window.weekday === day)
      .map(({ startTime, endTime }) => ({ startTime, endTime }));
  const checkedDays = WEEKDAYS.filter((day) => windowsOf(day).length > 0);

  const [mode, setMode] = React.useState<'always' | 'days'>(value.length > 0 ? 'days' : 'always');
  // Rozne godziny na starcie tylko wtedy, gdy zapisany rabat faktycznie je ma.
  const [perDay, setPerDay] = React.useState(
    () => new Set(checkedDays.map((day) => intervalsKey(windowsOf(day)))).size > 1,
  );
  // Wspolne godziny trzymamy osobno, zeby przetrwaly odznaczenie wszystkich dni.
  const [shared, setShared] = React.useState<Interval[]>(() =>
    checkedDays.length > 0 ? windowsOf(checkedDays[0]) : ALL_DAY,
  );

  const build = (days: Weekday[], intervalsFor: (day: Weekday) => Interval[]) =>
    WEEKDAYS.filter((day) => days.includes(day)).flatMap((day) =>
      intervalsFor(day).map((interval) => ({ ...interval, weekday: day })),
    );

  const intervalsForNewDay = () =>
    perDay && checkedDays.length > 0 ? windowsOf(checkedDays[0]) : shared;

  const setDays = (days: Weekday[]) =>
    onChange(
      build(days, (day) => (checkedDays.includes(day) ? windowsOf(day) : intervalsForNewDay())),
    );

  const toggleDay = (day: Weekday) =>
    setDays(
      checkedDays.includes(day) ? checkedDays.filter((d) => d !== day) : [...checkedDays, day],
    );

  const changeShared = (intervals: Interval[]) => {
    setShared(intervals);
    onChange(build(checkedDays, () => intervals));
  };

  const changeDay = (day: Weekday, intervals: Interval[]) =>
    onChange(build(checkedDays, (d) => (d === day ? intervals : windowsOf(d))));

  const togglePerDay = (next: boolean) => {
    setPerDay(next);
    // Powrot do wspolnych godzin: wszystkie dni dostaja godziny pierwszego zaznaczonego.
    if (!next && checkedDays.length > 0) changeShared(windowsOf(checkedDays[0]));
  };

  const presets: { key: string; days: Weekday[] }[] = [
    { key: 'presetWorkdays', days: WORKDAYS },
    { key: 'presetWeekend', days: WEEKEND },
    { key: 'presetAll', days: WEEKDAYS },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <RadioGroup
        row
        value={mode}
        onChange={(e) => {
          const next = e.target.value as 'always' | 'days';
          setMode(next);
          if (next === 'always') onChange([]);
        }}
      >
        <FormControlLabel
          value="always"
          control={<Radio size="small" />}
          label={t('discounts.scheduleEditor.always')}
        />
        <FormControlLabel
          value="days"
          control={<Radio size="small" />}
          label={t('discounts.scheduleEditor.days')}
        />
      </RadioGroup>

      {mode === 'days' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-4)',
            p: 'var(--space-4)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-sm)',
          }}
        >
          {/* Dni */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 'var(--space-2)',
              }}
            >
              <Typography sx={labelSx}>{t('discounts.scheduleEditor.daysLabel')}</Typography>
              <Box sx={{ display: 'flex', gap: 'var(--space-1)', flexWrap: 'wrap' }}>
                {presets.map((preset) => (
                  <Button
                    key={preset.key}
                    size="small"
                    variant={sameDays(checkedDays, preset.days) ? 'contained' : 'text'}
                    disableElevation
                    onClick={() => setDays(preset.days)}
                    sx={{ textTransform: 'none', minHeight: 28, py: 0 }}
                  >
                    {t(`discounts.scheduleEditor.${preset.key}`)}
                  </Button>
                ))}
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
                gap: 'var(--space-2)',
              }}
            >
              {WEEKDAYS.map((day) => {
                const checked = checkedDays.includes(day);
                return (
                  <Tooltip key={day} title={t(`discounts.weekdays.${day}`)}>
                    <ButtonBase
                      role="checkbox"
                      aria-checked={checked}
                      aria-label={t(`discounts.weekdays.${day}`)}
                      onClick={() => toggleDay(day)}
                      sx={{
                        height: 40,
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid',
                        borderColor: checked ? 'var(--color-primary)' : 'var(--color-border)',
                        backgroundColor: checked ? 'var(--color-primary)' : 'var(--color-surface)',
                        color: checked ? 'var(--color-text-inverse)' : 'var(--color-text-primary)',
                        fontSize: 'var(--text-sm)',
                        fontWeight: 600,
                        transition: 'background-color 120ms, border-color 120ms',
                        '&:hover': {
                          borderColor: 'var(--color-primary)',
                          backgroundColor: checked
                            ? 'var(--color-primary-hover)'
                            : 'var(--color-primary-soft)',
                        },
                      }}
                    >
                      {t(`discounts.weekdaysShort.${day}`)}
                    </ButtonBase>
                  </Tooltip>
                );
              })}
            </Box>
            {checkedDays.length === 0 && (
              <FormHelperText sx={{ m: 0 }}>
                {t('discounts.scheduleEditor.noDaysHint')}
              </FormHelperText>
            )}
          </Box>

          {/* Godziny */}
          {checkedDays.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 'var(--space-2)',
                }}
              >
                <Typography sx={labelSx}>{t('discounts.scheduleEditor.hoursLabel')}</Typography>
                {checkedDays.length > 1 && (
                  <FormControlLabel
                    sx={{ m: 0 }}
                    control={
                      <Switch checked={perDay} onChange={(e) => togglePerDay(e.target.checked)} />
                    }
                    label={
                      <Typography sx={{ fontSize: 'var(--text-sm)', ml: 'var(--space-2)' }}>
                        {t('discounts.scheduleEditor.perDay')}
                      </Typography>
                    }
                  />
                )}
              </Box>
              {perDay && checkedDays.length > 1 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  {checkedDays.map((day, index) => (
                    <Box
                      key={day}
                      sx={{
                        py: 'var(--space-2)',
                        borderTop: index === 0 ? 'none' : '1px solid var(--color-hairline)',
                      }}
                    >
                      <IntervalsEditor
                        dayLabel={t(`discounts.weekdays.${day}`)}
                        intervals={windowsOf(day)}
                        onChange={(intervals) => changeDay(day, intervals)}
                      />
                    </Box>
                  ))}
                </Box>
              ) : (
                <IntervalsEditor
                  intervals={checkedDays.length > 0 ? windowsOf(checkedDays[0]) : shared}
                  onChange={changeShared}
                />
              )}
            </Box>
          )}
        </Box>
      )}

      <FormHelperText error={Boolean(error)} sx={{ mt: 0 }}>
        {error ?? t('discounts.scheduleEditor.timeZoneHint', { zone: timeZone ?? 'Europe/Warsaw' })}
      </FormHelperText>
    </Box>
  );
};
